import React, { FC, memo } from 'react';

import Modal from 'react-modal';
import 'react-image-gallery/styles/css/image-gallery.css';

import { ModalCloseButton } from '@components/Modal/utils';
import Gallery from '@components/galleries/Gallery';
import { getTypedFeatureProps } from '@views/hotel/detail/utils/features';
import { useBreakpoint } from '@contexts/breakpoints';

import { FeatureLegendSection, RoomFeatures } from './FeatureSetRenderers';
import {
  Details,
  DetailsHeader,
  FeaturesBlock,
  GalleryContainer,
  ModalContent,
  ModalOverlay,
} from './styled';
import Mobile from './DetailsGalleriesModalMobile';
import { DetailsGalleriesModalProps, ModalView } from './types';
import SelectVariationTab from './SelectVariationTab';

const NEXT_JS_ROOT = '#__next';

if (typeof window !== 'undefined' && document.querySelector(NEXT_JS_ROOT)) {
  Modal.setAppElement(NEXT_JS_ROOT);
} else {
  console.warn(
    'Modal.setAppElement could not find the root element. Make sure this is called in a browser environment.'
  );
}

export { ModalView };
const DetailsGalleriesModal: FC<DetailsGalleriesModalProps> = ({
  galleries,
  title,
  closeModal,
  isOpen,
  view,
  room,
  roomOptionDetailProps,
  ...rest
}) => {
  const { xl } = useBreakpoint();
  const featuresProps = getTypedFeatureProps(rest);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      shouldReturnFocusAfterClose={false}
      overlayElement={(props, contentElement) => (
        <ModalOverlay {...props}>{contentElement}</ModalOverlay>
      )}
      contentElement={
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ style, ...rest }, children) => (
          <ModalContent {...rest}>{children}</ModalContent>
        )
      }
    >
      {!xl ? (
        <Mobile
          closeModal={closeModal}
          galleries={galleries}
          view={view}
          roomOptionDetailProps={roomOptionDetailProps}
          room={room}
          {...featuresProps}
          {...rest}
        />
      ) : (
        <GalleryContainer>
          <Gallery
            galleries={galleries}
            title={title ?? ''}
            closeGallery={closeModal}
            hideCategoryAll={rest.entityType === 'room'}
          />
        </GalleryContainer>
      )}

      {rest.entityType === 'room' || rest.entityType === 'roomOptions' ? (
        <Details>
          {rest.entityType === 'room' ? (
            <FeaturesBlock>
              <DetailsHeader>
                <ModalCloseButton onClick={closeModal} />
              </DetailsHeader>
              <FeatureLegendSection />

              <RoomFeatures features={rest.features} room={room} />
            </FeaturesBlock>
          ) : null}

          {rest.entityType === 'roomOptions' && roomOptionDetailProps ? (
            <FeaturesBlock>
              <DetailsHeader>
                <ModalCloseButton onClick={closeModal} />
              </DetailsHeader>
              <SelectVariationTab
                openDatePicker={() => {
                  roomOptionDetailProps.openDatePicker();
                  closeModal?.();
                }}
                roomOptionDetailProps={roomOptionDetailProps}
                onSelectDates={() => {
                  roomOptionDetailProps?.onSelectDates?.();
                  closeModal?.();
                }}
                onSelectVariation={(v) => {
                  roomOptionDetailProps.handleSelectVariation(v);
                  closeModal?.();
                }}
                features={rest.features}
                room={room}
              />
            </FeaturesBlock>
          ) : null}
        </Details>
      ) : null}
    </Modal>
  );
};

export default memo(DetailsGalleriesModal);
